import { Component, ElementRef, OnInit } from '@angular/core';
import { Log } from '@s8l/client-tree-lib';

import { BgAiServerService, TutorSummary, TutorTotals } from 'src/app/services/jsonrpc/bg-ai-server.service';

import { HomeWidget } from '../dashboard-widget.utils';

/**
 * AI tutor usage (tender C4, BSNS-25; layout docs/c4-analytics-design.md): an overview of the
 * namespace and a table by module, from the namespace's bg-ai-server. Shown only when the tutor
 * is enabled in the namespace and the user may read the dashboard data (admins and the
 * Analytics role, `stats` READ); otherwise the widget renders nothing.
 */
@Component({
  selector: 'app-tutor-usage-widget',
  templateUrl: './tutor-usage-widget.component.html',
  styleUrls: ['./tutor-usage-widget.component.scss']
})
export class TutorUsageWidgetComponent extends HomeWidget implements OnInit {
  public summary: TutorSummary = null;
  public modules: TutorTotals[] = [];
  /** Hidden until the server says the tutor is enabled and the user may see it. */
  public visible = false;
  public showAll = false;

  constructor(elRef: ElementRef, private ai: BgAiServerService) {
    super(elRef);
  }

  ngOnInit() {
    // bg-ai-server accepts at most 400 days (tutor events are kept 180); a longer dashboard
    // range is narrowed, and the card shows the period actually used.
    const to = this.settings.to instanceof Date ? this.settings.to : new Date();
    const earliest = new Date(to.getTime() - 399 * 24 * 3600 * 1000);
    const from = this.settings.from instanceof Date && this.settings.from > earliest ? this.settings.from : earliest;
    const range = {
      from: TutorUsageWidgetComponent.isoDate(from),
      to: TutorUsageWidgetComponent.isoDate(to)
    };
    Promise.all([this.ai.analyticsSummary(range), this.ai.analyticsByModule(range)])
      .then(([summary, byModule]) => {
        this.summary = summary;
        this.modules = byModule.modules ?? [];
        this.visible = summary.enabled === true;
      })
      .catch(err => {
        // No permission, tutor not deployed in this namespace, or the server is down: show nothing.
        this.visible = false;
        Log.log('TutorUsageWidgetComponent', 'Error', err);
      });
  }

  /** Local calendar date as yyyy-mm-dd, as the dashboard's date picker means it. */
  static isoDate(value: unknown): string | undefined {
    if (!(value instanceof Date) || isNaN(value.getTime())) {
      return undefined;
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }

  public get shownModules(): TutorTotals[] {
    return this.showAll ? this.modules : this.modules.slice(0, 20);
  }

  public percent(part: number, total: number): string {
    return total > 0 ? `${Math.round((100 * part) / total)} %` : '–';
  }

  public seconds(ms?: number): string {
    return ms == null ? '–' : `${(ms / 1000).toFixed(1)} s`;
  }

  public usd(micros?: number): string {
    return micros == null ? '–' : `${(micros / 1e6).toFixed(2)} USD`;
  }

  /** Last path segment, decoded: the module name as editors know it. */
  public moduleName(path?: string): string {
    const last = (path ?? '').split('/').pop() ?? '';
    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  }
}
